import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  signal,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgStyle } from '@angular/common';
import { Router } from '@angular/router';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { ShoppingCartService } from '../../../../services/shopping-cart/shopping-cart.service';
import { OrderDirectTicketSupportService } from '../../../../services/support/order-ticket-support/order-direct-ticket-support.service';
import { OnDemandAvailabilityStore } from '../../../../services/api/on-demand/on-demand-availability-store.service';
import { OperatorMappingStore } from '../../../../services/api/operator-mapping/operator-mapping-store.service';
import { ReconRouteStore } from '../../../../services/api/journey-planning/recon/recon-route-store.service';
import { OnDemandOptionsFormService } from '../../../../services/on-demand/on-demand-options-form.service';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { DateFormatSupport } from '../../../../services/support/date-format-support/date-format-support.service';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { OnDemandRelationProductGroupStore } from '../../../../services/api/on-demand/on-demand-relation-product-group-store.service';
import { OnDemandSelectionStateService } from '../../../../services/on-demand/on-demand-selection-state.service';
import {
  EEntityType,
  ERelationType,
  EFahrgasttyp,
  IJourneyLeg,
  IOnDemandPassengerOptionsForm,
  TariffProductGroup,
  TariffSelectionAttribute,
} from '@web/dtos';
import { ERoute } from '../../../../constants/routes.enum';
import { formatIsoTime, formatSimpleTime } from '@web/shared-functions/date';
import {
  getTransportName,
  getTransferLabel,
  isTransferLeg,
} from '@web/shared-functions/journey';
import { resolveConnectionOptionSelection } from '@web/shared-functions/on-demand';

@Component({
  selector: 'aw-on-demand-ticket-configuration-options',
  templateUrl: './on-demand-ticket-configuration-options.component.html',
  imports: [
    ReactiveFormsModule,
    NgStyle,
    DatePipe,
    FilterTextsPipe,
    LanguagePipe,
    ButtonComponent,
    DropDownComponent,
  ],
})
export class OnDemandTicketConfigurationOptionsComponent {
  readonly ticketName: InputSignal<string> = input.required();

  protected readonly corporateDesign = inject(CorporateDesignService);
  protected readonly shoppingCart = inject(ShoppingCartService);
  protected readonly availabilityStore = inject(OnDemandAvailabilityStore);
  protected readonly operatorMappingStore = inject(OperatorMappingStore);
  protected readonly reconRouteStore = inject(ReconRouteStore);
  protected readonly textStore = inject(WebTextStore);
  protected readonly selectionState = inject(OnDemandSelectionStateService);
  protected readonly relationProductGroupStore = inject(
    OnDemandRelationProductGroupStore
  );
  protected readonly dateFormatSupport = inject(DateFormatSupport);

  private readonly directTicketOrderService = inject(
    OrderDirectTicketSupportService
  );
  private readonly textFilterPipe = inject(FilterTextsPipe);
  private readonly languagePipe = inject(LanguagePipe);

  private readonly optionsFormService = inject(OnDemandOptionsFormService);
  private readonly router = inject(Router);

  protected readonly ERoute = ERoute;
  protected readonly EEntityType = EEntityType;
  protected readonly ERelationType = ERelationType;
  protected readonly getTransportName: typeof getTransportName =
    getTransportName;
  protected readonly getTransferLabel: typeof getTransferLabel =
    getTransferLabel;
  protected readonly isTransferLeg: typeof isTransferLeg = isTransferLeg;

  protected readonly passengerFormArray = new FormArray<
    FormGroup<IOnDemandPassengerOptionsForm>
  >([]);
  protected get passengerForms() {
    return this.passengerFormArray.controls;
  }

  protected readonly extraCalculationTrigger = signal<number>(0);
  readonly validationError = signal<string | null>(null);
  protected readonly isNavigating = signal(false);

  readonly ticketConfiguration = computed(
    () =>
      this.relationProductGroupStore.firstProductGroup() ??
      (this.shoppingCart.directTicketConfiguration() as
        | TariffProductGroup
        | undefined)
  );

  protected readonly isLoading = computed(
    () =>
      this.shoppingCart.isLoadingTicketConfiguration() ||
      this.relationProductGroupStore.isLoading() ||
      this.availabilityStore.isLoading() ||
      this.operatorMappingStore.isLoading() ||
      this.reconRouteStore.isLoading()
  );

  protected readonly connectionOptions =
    this.availabilityStore.connectionOptions;
  protected readonly isAvailable = this.availabilityStore.isAvailable;
  protected readonly rejectionReason = this.availabilityStore.rejectionReason;
  protected readonly matchedMapping = this.availabilityStore.matchedMapping;
  protected readonly trip = this.reconRouteStore.reconRoute;
  protected readonly tripDate = computed(() => this.trip()?.origin?.date);

  private readonly noExistingTicketLabel = computed(() =>
    this.languagePipe.transform(
      this.textFilterPipe.transform(
        this.textStore.texts(),
        ERelationType.noExistingTicketLabel,
        EEntityType.onDemandTicketConfiguration
      )
    )
  );
  private readonly remainingLabel = computed(() =>
    this.languagePipe.transform(
      this.textFilterPipe.transform(
        this.textStore.texts(),
        ERelationType.remaining,
        EEntityType.onDemandTicketConfiguration
      )
    )
  );

  protected readonly existingTicketOptions = computed(() => {
    const options = this.matchedMapping()?.dropDownOptions ?? [];
    if (options.length > 0) {
      return [
        { id: 'default', name: this.noExistingTicketLabel() },
        ...options,
      ];
    }

    return [];
  });
  protected readonly showExistingTicketDropdown = computed(
    () => this.existingTicketOptions().length > 1
  );

  protected readonly bookableServiceOptions = computed(
    () =>
      this.selectionState.selectedConnectionOption()?.bookableServiceOptions ??
      []
  );

  constructor() {
    this.shoppingCart.productForms.updateValueAndValidity();
    this.directTicketOrderService.setCurrentProductDefinition();
    this.directTicketOrderService.tryToAddToCart();

    effect(() => {
      if (
        !this.operatorMappingStore.isLoading() &&
        !this.reconRouteStore.isLoading()
      ) {
        this.shoppingCart.loadTicketConfiguration(this.ticketName());
      }
    });

    effect(() => {
      if (!this.isLoading() && this.passengerFormArray.length === 0)
        this.addPassenger();
    });

    effect(() => {
      if (!this.operatorMappingStore.isLoading() && this.trip()) {
        this.availabilityStore.triggerAvailabilityRequest(
          this.passengerFormArray.length || 1
        );
      }
    });

    effect(() => {
      const options = this.connectionOptions();
      const leg = this.availabilityStore.onDemandLeg();

      const selected = resolveConnectionOptionSelection(
        options,
        leg,
        this.selectionState.selectedConnectionOption()
      );
      if (selected !== this.selectionState.selectedConnectionOption()) {
        this.selectionState.selectConnectionOption(selected ?? null);
      }
    });
  }

  protected readonly showFahrgasttyp = computed(() =>
    (this.trip()?.legs ?? []).some(
      leg => !this.availabilityStore.isOnDemandLeg(leg)
    )
  );

  protected readonly fahrgasttypOptions = computed(() => {
    const attr = this.ticketConfiguration()?.selectionAttributes?.find(
      a => a.name.toLowerCase() === 'fahrgasttyp'
    );
    return (
      (attr?.values?.filter(v => typeof v === 'string') as string[]) || [
        EFahrgasttyp.ERWACHSENER,
        EFahrgasttyp.KIND,
      ]
    );
  });

  protected addPassenger(): void {
    const initialValues =
      this.ticketConfiguration()
        ?.selectionAttributes.filter(
          (a: TariffSelectionAttribute) => a.name.toLowerCase() !== 'name'
        )
        .map((a: TariffSelectionAttribute) => a.values[0]) ?? [];

    const newForm =
      this.optionsFormService.buildPassengerOptionsForm(initialValues);
    const types = this.fahrgasttypOptions();

    if (types.length > 0) {
      newForm.controls.fahrgasttyp.setValue(
        types[0] || EFahrgasttyp.ERWACHSENER
      );
    }

    newForm.controls.existingTicketType.setValue(this.noExistingTicketLabel());
    newForm.controls.existingTicketType.valueChanges.subscribe(value =>
      this.onExistingTicketSelected(newForm, value)
    );
    this.passengerFormArray.push(newForm);
    this.extraCalculationTrigger.update(v => v + 1);
  }

  protected removePassenger(index: number): void {
    if (index === 0 || this.passengerFormArray.length <= 1) return;

    this.passengerFormArray.removeAt(index);
    this.selectionState.passengerCount.set(this.passengerFormArray.length);
    this.extraCalculationTrigger.update(value => value + 1);
  }

  protected addExtra(form: FormGroup<IOnDemandPassengerOptionsForm>): void {
    this.optionsFormService.addExtra(form);
    const extraForm = form.controls.selectedExtras.at(
      form.controls.selectedExtras.length - 1
    );
    extraForm.controls.publicTransportXml.valueChanges.subscribe(value =>
      this.onExtraChange(value, extraForm)
    );
    this.extraCalculationTrigger.update(v => v + 1);
  }

  protected removeExtra(
    form: FormGroup<IOnDemandPassengerOptionsForm>,
    index: number
  ): void {
    this.optionsFormService.removeExtra(form, index);
    this.extraCalculationTrigger.update(v => v + 1);
  }

  protected get allFormsValid(): boolean {
    return (
      this.passengerFormArray.controls.every(f => f.valid) &&
      !!this.selectionState.selectedConnectionOption()
    );
  }

  protected onExtraChange(value: string, extraForm: FormGroup): void {
    const label =
      this.bookableServiceOptions().find(
        service => service.mobilityUserField === value
      )?.label ?? '';
    extraForm.controls['name'].setValue(label);
    this.extraCalculationTrigger.update(v => v + 1);
  }

  protected getAvailableBookableServiceOptions(pIdx: number, eIdx: number) {
    const selectedValue =
      this.passengerForms[pIdx]?.controls.selectedExtras.at(eIdx)?.controls
        .publicTransportXml.value;

    return this.bookableServiceOptions()
      .filter(
        service =>
          service.mobilityUserField === selectedValue ||
          !this.isExtraDisabled(service.mobilityUserField, pIdx, eIdx)
      )
      .map(service => ({
        ...service,
        label: `${service.label} (${this.remainingLabel()} ${this.getRemainingCapacity(
          service.mobilityUserField,
          pIdx,
          eIdx
        )})`,
      }));
  }

  protected getRemainingCapacity(
    mobilityUserField: string,
    pIdx: number,
    eIdx: number
  ): number {
    this.extraCalculationTrigger();
    let used = 0;

    this.passengerForms.forEach((pForm, currentPIdx) => {
      pForm.controls.selectedExtras.controls.forEach((eForm, currentEIdx) => {
        if (currentPIdx === pIdx && currentEIdx === eIdx) return;
        if (eForm.value.publicTransportXml === mobilityUserField) used++;
      });
    });

    const max =
      this.bookableServiceOptions().find(
        s => s.mobilityUserField === mobilityUserField
      )?.maxCapacity ?? 0;
    return max - used;
  }

  protected isExtraDisabled(
    mobilityUserField: string,
    pIdx: number,
    eIdx: number
  ): boolean {
    return this.getRemainingCapacity(mobilityUserField, pIdx, eIdx) <= 0;
  }

  protected getDisplayDeparture(
    leg: IJourneyLeg,
    previousLeg?: IJourneyLeg
  ): string {
    if (this.availabilityStore.isOnDemandLeg(leg)) {
      return formatIsoTime(
        this.selectionState.selectedConnectionOption()?.departureTimetabled
      );
    }
    if (
      this.isTransferLeg(leg) &&
      previousLeg &&
      this.availabilityStore.isOnDemandLeg(previousLeg)
    ) {
      return this.selectionState.confirmedArrival();
    }
    return formatSimpleTime(leg.origin?.time ?? '');
  }

  protected getTransferDepartureTime(nextLeg?: IJourneyLeg): string | null {
    if (nextLeg && this.availabilityStore.isOnDemandLeg(nextLeg)) {
      return this.selectionState.confirmedDeparture();
    }

    return null;
  }

  protected getTransferArrivalTime(previousLeg?: IJourneyLeg): string | null {
    if (previousLeg && this.availabilityStore.isOnDemandLeg(previousLeg)) {
      return this.selectionState.confirmedArrival();
    }

    return null;
  }

  protected getFinalDestinationTime(): string {
    const trip = this.trip();
    if (!trip) return '';
    const lastLeg = trip.legs[trip.legs.length - 1];

    if (this.availabilityStore.isOnDemandLeg(lastLeg)) {
      return formatIsoTime(
        this.selectionState.selectedConnectionOption()?.arrivalTimetabled
      );
    }
    return formatSimpleTime(trip.destination?.time ?? '');
  }

  protected onExistingTicketSelected(
    form: FormGroup<IOnDemandPassengerOptionsForm>,
    value: string | null
  ): void {
    const hasTicket = !!value && value !== this.noExistingTicketLabel();
    form.controls.useExistingTicket.setValue(hasTicket);
  }

  protected async proceedToReservation(): Promise<void> {
    this.validationError.set(null);
    this.isNavigating.set(true);

    const extras = this.passengerForms.map(
      f =>
        f.value.selectedExtras
          ?.map(e => e.publicTransportXml ?? '')
          .filter(Boolean) ?? []
    );
    this.selectionState.extrasPerPassenger.set(extras);

    const option = this.selectionState.selectedConnectionOption();
    if (option) {
      const validation = await this.availabilityStore.validateSelection(
        this.passengerForms.length,
        extras,
        option.departureTimetabled,
        option.arrivalTimetabled
      );

      if (!validation.isValid) {
        this.validationError.set(
          validation.error ?? 'Verbindung nicht mehr verfügbar.'
        );
        this.isNavigating.set(false);
        return;
      }
    }

    this.selectionState.passengerTicketSelections.set(
      this.passengerForms.map(f => {
        let existingTicketType: string | null = null;
        if (f.value.useExistingTicket) {
          existingTicketType = f.value.existingTicketType || null;
        }

        return {
          fahrgasttyp: f.value.fahrgasttyp || EFahrgasttyp.ERWACHSENER,
          useExistingTicket: !!f.value.useExistingTicket,
          existingTicketType,
        };
      })
    );

    this.selectionState.passengerCount.set(this.passengerForms.length);

    await this.router.navigate([
      `/${ERoute.onDemandTicketConfiguration}/${ERoute.reservation}`,
    ]);
  }
}
